import { MoreVert } from '@mui/icons-material';
import { Box, Button, IconButton, Menu, MenuItem, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import Icon from '/@/components/icon';
import useUnitProvider from '/@/core/context/unit';
import { useGetUserNode } from '/@/hooks/nodes';
import { _ } from '/@/locale';
import type { CropMonitoring } from '/@/types/crop-monitoring';
import type { CropMonitoringNote } from '/@/types/crop-monitoring-note';
import { formatDate } from '/@/utils';
import { reverse } from '/@/utils/router';
import { getUserDisplayName, RegistryFooter } from './card-footer';
import { RegistryCard } from './registry-card';
import RegistryNotes from './registry-notes';

const Cell = ({
    title,
    value,
    highlighted = false,
}: {
    title: string;
    value: string | number;
    highlighted?: boolean;
}) => (
    <Stack
        direction='column'
        spacing={0.5}
        sx={{ p: 1, backgroundColor: highlighted ? 'primary.mui.stroke' : 'grey.100' }}
    >
        <Typography variant='caption' color='primary'>
            {title}
        </Typography>
        <Typography variant='subtitle1' color='primary' fontWeight='bold'>
            {value}
        </Typography>
    </Stack>
);

const RegistryHarvest = ({
    cropMonitoring,
    notes,
    onEdit,
}: {
    cropMonitoring: CropMonitoring;
    notes: CropMonitoringNote[];
    /** Opens the harvest-report flow (`CreateReportModal`) to edit this crop monitoring's result. */
    onEdit?: () => void;
}) => {
    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
    const navigate = useNavigate();
    const units = useUnitProvider();

    const fromFieldview = !!cropMonitoring.result?.harvestReport;
    const closeMenu = () => setMenuAnchor(null);

    const handleEdit = () => {
        closeMenu();
        onEdit?.();
    };

    const handleViewReport = () => {
        navigate(
            reverse('cropMonitoring.CornCropMonitoringDetail.report', {
                params: { cropMonitoringId: cropMonitoring.id },
            })
        );
    };

    const harvestDateDisplay = cropMonitoring.harvestDate ? formatDate(new Date(cropMonitoring.harvestDate)) : '—';
    const harvestedArea =
        cropMonitoring.result?.totalHarvestedArea != null
            ? units.toArea(cropMonitoring.result.totalHarvestedArea, 2).toString()
            : '—';
    const humidity =
        cropMonitoring.result?.humidityAtHarvest != null
            ? `${cropMonitoring.result.humidityAtHarvest.toFixed(2)}%`
            : '—';
    const averageYield =
        cropMonitoring.result?.averageYield != null
            ? units.toYield(cropMonitoring.result.averageYield, cropMonitoring.crop, 2).toString()
            : '—';

    const userNodeQuery = useGetUserNode(cropMonitoring.result?.createdBy ?? 0);
    const authorName = getUserDisplayName(userNodeQuery.data);
    const registeredAt = cropMonitoring.result?.created ? formatDate(new Date(cropMonitoring.result.created)) : null;

    return (
        <RegistryCard>
            <Stack direction='row' justifyContent='space-between' alignItems='center'>
                <Stack direction='row' spacing={1} alignItems='center'>
                    {fromFieldview && <Icon type='fieldview' size='xs' />}
                    <Typography variant='h6' color='primary'>
                        {_('Cosecha')}
                    </Typography>
                </Stack>
                <IconButton size='small' aria-label={_('Más opciones')} onClick={(e) => setMenuAnchor(e.currentTarget)}>
                    <MoreVert fontSize='small' />
                </IconButton>
                <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
                    <MenuItem onClick={handleEdit} disabled={!onEdit}>
                        {_('Editar')}
                    </MenuItem>
                </Menu>
            </Stack>

            <Box
                sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1, overflow: 'auto', width: '100%' }}
            >
                <Cell title={_('Fecha de cosecha')} value={harvestDateDisplay} />
                <Cell title={_('Hectáreas cosechadas')} value={harvestedArea} />
                <Cell title={_('Humedad de cosecha')} value={humidity} />
                <Cell title={_('Rendimiento total')} value={averageYield} highlighted />
            </Box>

            <RegistryNotes notes={notes} fieldId={cropMonitoring.field} closedCropMonitoringId={cropMonitoring.id} />

            <Stack alignItems='flex-end'>
                <Button variant='contained' color='primary' onClick={handleViewReport}>
                    {_('Ver reporte')}
                </Button>
            </Stack>

            {fromFieldview ? (
                <Typography variant='caption' color='primary.mui.inactive'>
                    {_('Sincronizado desde FieldView')}
                </Typography>
            ) : (
                registeredAt && <RegistryFooter authorName={authorName} date={registeredAt} />
            )}
        </RegistryCard>
    );
};

export default RegistryHarvest;
